/* ──────────────────────────────────────────────────────────────────────
 * ContinuousExpandFlow.jsx
 *
 * Single-track audio-driven flow that replaces the separate
 *   expand → rapid → immersion handoff with one continuous experience.
 *
 * Audio file (one continuous voice take, 98s):
 *   /api/unified-audio/files/voice/expand_continuous_v1.mp3
 *
 * Cues are synchronised to the *actual* spoken content of the recording
 * (transcribed via Whisper):
 *
 *   0:00 – 0:30   "expand"     — breathing orb + matching narration text
 *                                 (Pause / breathe / soften / relax /
 *                                  keep looking forward but relax focus)
 *   0:30 – 0:33   "crossfade"  — orb fades to single fixation dot (3s)
 *   0:33 – 0:41   "dot_silent" — dot visible while audio says
 *                                 "Trust that all the information is
 *                                  effortlessly being stored…"
 *   0:41 – 1:27   "flashes"    — audio says "Just keep looking at the
 *                                 dot and relax"; word/answer flashes
 *                                 begin under the dot
 *   1:27 – 1:34   "tapering"   — flashes slow + dot expands as audio
 *                                 says "Now you're ready to go even
 *                                  deeper"
 *   1:34 +        "handoff"    — onComplete() → ImmersionPlayer
 *
 * Cue points are anchored to absolute seconds for the canonical 98s
 * take, and *only* fall back to ratios if a different-length recording
 * is wired in. ────────────────────────────────────────────────────── */

import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RapidInstallation from "./RapidInstallation";
import { JOURNEY_FONT_HEAD, JOURNEY_FONT_LABEL } from "./journeyTheme";
import { voiceVolume, getStoredAudioMode } from "../../utils/modeAudio";

// Canonical recording length the cues are tuned for.
const CANON_DURATION = 98;

// Absolute cue seconds, hand-tuned to the spoken content of the
// canonical 98s expand take.
const CUE_ABS = {
  CROSSFADE_START:  30,   // orb starts fading after "relax your focus"
  DOT_SILENT_START: 33,   // dot fully present, audio still narrating
  FLASHES_START:    41,   // audio: "Just keep looking at the dot…"
  TAPERING_START:   87,   // audio: "Now you're ready to go even deeper"
  HANDOFF:          95,   // ~3s before audio ends → onComplete
};

// Spoken-line landmarks (s) inside the canonical track.
const NARRATION_ABS = [
  { at: 0.5,  text: "Pause and be present" },
  { at: 7.0,  text: "Take a slow breath into your belly" },
  { at: 14.5, text: "Soften your gaze" },
  { at: 22.5, text: "Relax your eyes" },
  { at: 27.5, text: "Keep looking forward, but relax your focus" },
];

// If the audio length deviates significantly from canonical, fall back
// to scaling. Otherwise we use the absolute, exact-match cues.
function buildCues(durationSec) {
  const D = durationSec && durationSec > 5 ? durationSec : CANON_DURATION;
  const drift = Math.abs(D - CANON_DURATION) / CANON_DURATION;
  if (drift < 0.06) return { ...CUE_ABS };
  const scale = D / CANON_DURATION;
  return {
    CROSSFADE_START:  CUE_ABS.CROSSFADE_START  * scale,
    DOT_SILENT_START: CUE_ABS.DOT_SILENT_START * scale,
    FLASHES_START:    CUE_ABS.FLASHES_START    * scale,
    TAPERING_START:   CUE_ABS.TAPERING_START   * scale,
    HANDOFF:          CUE_ABS.HANDOFF          * scale,
  };
}

function buildNarration(durationSec) {
  const D = durationSec && durationSec > 5 ? durationSec : CANON_DURATION;
  const drift = Math.abs(D - CANON_DURATION) / CANON_DURATION;
  if (drift < 0.06) return NARRATION_ABS;
  const scale = D / CANON_DURATION;
  return NARRATION_ABS.map(({ at, text }) => ({ at: at * scale, text }));
}

export default function ContinuousExpandFlow({
  audioUrl,
  content,
  slug,
  day,
  onComplete,
}) {
  const audioRef = useRef(null);
  const [phase, setPhase] = useState("expand");
  const [elapsed, setElapsed] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  // Start audio on mount (user gesture upstream — they tapped Continue
  // on the previous step). Cue points are computed from the actual
  // audio.duration once it's known so the visual flow adapts to whatever
  // recording is in place. Handoff is driven by the audio's own `ended`
  // event so the full "Now you're ready to go even deeper" line plays
  // through without ever being cut off.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioUrl) return;
    el.volume = voiceVolume(getStoredAudioMode());
    el.play().catch(() => {});
    const onMeta = () => setAudioDuration(el.duration || 0);
    const onTime = () => {
      const t = el.currentTime || 0;
      setElapsed(t);
      const CUES = buildCues(el.duration || audioDuration);
      if (t < CUES.CROSSFADE_START) setPhase("expand");
      else if (t < CUES.DOT_SILENT_START) setPhase("crossfade");
      else if (t < CUES.FLASHES_START) setPhase("dot_silent");
      else if (t < CUES.TAPERING_START) setPhase("flashes");
      else setPhase("tapering");
    };
    const onEnded = () => {
      setPhase("handoff");
      onComplete?.();
    };
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, onComplete]);

  // Narration overlay lines — anchored to the actual spoken timestamps
  // of the canonical 98s take (with graceful scaling if the wired
  // recording deviates by more than ~6%).
  const NARRATION_LINES = useMemo(
    () => buildNarration(audioDuration),
    [audioDuration]
  );

  // Active narration line based on elapsed
  const activeLine = useMemo(() => {
    let cur = null;
    for (const l of NARRATION_LINES) {
      if (elapsed >= l.at) cur = l;
    }
    // Hold each line ~5.5s on screen, then clear it (keeps the screen
    // calm during the pauses between spoken lines).
    if (cur && elapsed > cur.at + 5.5) return null;
    return cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed, NARRATION_LINES]);

  return (
    <div
      className="fixed inset-0 z-[80] overflow-hidden"
      style={{ background: "#0D1B2A" }}
      data-testid="continuous-expand-flow"
    >
      {/* Hidden audio — single continuous take.
          crossOrigin intentionally NOT set — same-origin endpoint
          without CORS headers can fail iOS audio decode silently. */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        playsInline
        muted={muted}
      />

      {/* ═══ PHASE 1 — EXPAND ═══ breathing orb + narration ═══════════ */}
      <AnimatePresence>
        {(phase === "expand" || phase === "crossfade") && (
          <motion.div
            key="expand-layer"
            className="absolute inset-0 flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "crossfade" ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, ease: "easeInOut" }}
          >
            {/* Breathing orb */}
            <motion.div
              className="rounded-full"
              style={{
                width: 180, height: 180,
                background:
                  "radial-gradient(circle at 50% 45%, rgba(190,225,255,0.65) 0%, rgba(77,163,255,0.32) 35%, rgba(13,27,42,0) 70%)",
                filter: "blur(2px)",
              }}
              animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* Soft narration overlay */}
            <div className="mt-12 px-8 min-h-[3.6em] flex items-center justify-center">
              <AnimatePresence mode="wait">
                {activeLine && (
                  <motion.p
                    key={activeLine.at}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 0.86, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center italic"
                    style={{
                      color: "#FFFFFF",
                      fontFamily: JOURNEY_FONT_HEAD,
                      fontSize: "clamp(20px, 4.2vw, 26px)",
                      fontWeight: 300,
                      letterSpacing: "0.005em",
                      textShadow: "0 4px 22px rgba(77,163,255,0.32)",
                      maxWidth: "22ch",
                    }}
                    data-testid={`expand-line-${activeLine.at}`}
                  >
                    {activeLine.text}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ PHASE 2 — DOT-ONLY HOLD ═══ pure black + fixation dot ════
          During `flashes` phase the dot lifts upward so it sits ABOVE
          the flash band (which lives at top:55% → bottom:6%). */}
      {(phase === "dot_silent" || phase === "flashes" || phase === "tapering") && (
        <div
          className="absolute inset-0 flex items-start justify-center pt-[28%]"
          style={{
            transition: "padding-top 1.6s ease",
            paddingTop: phase === "dot_silent" ? "44vh" : "26vh",
          }}
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{
              scale: phase === "tapering" ? 1.6 : 1,
              opacity: 1,
            }}
            transition={{ duration: phase === "tapering" ? 8 : 1.5, ease: "easeInOut" }}
            className="rounded-full flex items-center justify-center"
            style={{
              width: 14, height: 14,
              background: "rgba(255,255,255,0.95)",
              boxShadow: "0 0 22px rgba(190,225,255,0.55)",
            }}
            data-testid="continuous-fixation-dot"
          >
            {/* Tiny R sigil inside the orb — Reclaim's signature at
                the centre of every immersion. Sits on top of the
                breathing dot, scales with it via the parent transform. */}
            <span
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontSize: 9,
                fontWeight: 600,
                lineHeight: 1,
                color: "rgba(20, 40, 80, 0.85)",
                letterSpacing: "0.02em",
                transform: "translateY(-0.5px)",
                pointerEvents: "none",
                userSelect: "none",
              }}
              data-testid="continuous-fixation-dot-sigil"
            >
              R
            </span>
          </motion.div>
        </div>
      )}

      {/* ═══ PHASE 3 — FLASHES UNDER DOT ═══════════════════════════════ */}
      {phase === "flashes" && (
        <div className="absolute inset-x-0 bottom-[6%] top-[55%] pointer-events-none" data-testid="continuous-flash-band">
          {/* Reuse the existing RapidInstallation flash deck in flashOnly
              mode — hides its own fixation dot, cue, and step label so
              we don't double up with the continuous-fixation-dot above. */}
          <RapidInstallation
            content={content}
            slug={slug}
            day={day}
            flashOnly
            onContinue={() => { /* handled by audio cue, ignored */ }}
          />
        </div>
      )}

      {/* Mute toggle — top-right, very subtle */}
      <button
        onClick={() => setMuted((m) => !m)}
        className="absolute top-5 right-5 text-[10px] tracking-[0.4em] uppercase opacity-50 hover:opacity-90 transition-opacity"
        style={{ color: "rgba(255,255,255,0.65)", fontFamily: JOURNEY_FONT_LABEL }}
        data-testid="continuous-expand-mute-btn"
      >
        {muted ? "unmute" : "mute"}
      </button>
    </div>
  );
}
